/**
 * FamilyDashBoard v13 — Alerts Card (צבע אדום / Tzeva Adom)
 *
 * Polls the Tzeva Adom alert history API with adaptive intervals:
 *   60s when alerts active, 5min when idle.
 * On new alert: plays a beep and shows a desktop notification if granted.
 * Renders scrolling list of recent alert events.
 *
 * X12/X15 protocol adopted (see ADR-071). */

import {
  INTERVALS,
  THREAT_LABELS,
  API,
  PROXIES,
  WORKER_BASE_URL,
  isWorkerEnabled,
} from "../../core/constants";
import "./alerts.css";
import { cGetStale } from "../../core/cache";
import { setSync, syncBurst, recordSuccess, recordFailure } from "../../core/sync";
import { isPageVisible } from "../../core/idle";
import { diagLog } from "../../core/diag";
import { fetchWithTimeout } from "../../core/fetch";
import { historyAppend, historyGet, sparklineSvg } from "../../core/history";
import { trustedHTML } from "../../core/trusted-types";
import type { AlertEvent, AlertsResponse } from "../../types/api";
import { isAlertEvent } from "../../types/api";
import type { CardConfigField } from "../../types/card";
import { setCardSignal } from "../../core/card-signal-protocol";
import { fromEpochSec } from "../../core/temporal";
import { registerSemanticProducer } from "../../core/semantic-clipboard";
import type { SemanticPayload } from "../../types/semantic-clipboard";
import { markFresh, renderFreshnessBadge } from "../../core/freshness";

// X15: cached snapshot of active alerts for the semantic-clipboard producer.
let _activeAlertsSnapshot: { count: number; areas: string[]; latestTs: number } | null = null;

function buildAlertsPayload(): SemanticPayload | null {
  const s = _activeAlertsSnapshot;
  if (!s || s.count === 0) return null;
  const areasText = s.areas.slice(0, 5).join(", ");
  return {
    cardId: "alerts",
    text: `צבע אדום · ${String(s.count)} התראות פעילות · ${areasText}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SpecialAnnouncement",
      name: "צבע אדום",
      datePosted: fromEpochSec(s.latestTs).toISOString(),
      category: "https://schema.org/CivicEmergencyAnnouncement",
      spatialCoverage: s.areas,
    },
    ts: Date.now(),
  };
}

// ── State ──
let _enabled = true;
let _lastAlertId: string | number | null = null;
let _haveActive = false;
let _unread = 0;
let _timer: ReturnType<typeof setTimeout> | null = null;
let _realtimeMode = false;
let _sse: EventSource | null = null;
let _beepVolume = 18; // 0-100, matches config.alertVolume default
const _baseTitle = document.title || "FamilyDashBoard";

function updateTitle(): void {
  document.title = _unread > 0 ? `⚠️ (${_unread}) ${_baseTitle}` : _baseTitle;
}

/** Set alert beep volume (0–100). Persists for the session. */
export function setAlertVolume(vol: number): void {
  _beepVolume = Math.max(0, Math.min(100, vol));
}

/** Get current alert beep volume. */
export function getAlertVolume(): number {
  return _beepVolume;
}

// ── DOM cache ──
let elScroll: HTMLElement | null = null;
let elBadge: HTMLElement | null = null;
let elSpark: SVGElement | null = null;

const ALERT_INTERVAL_RT = 10_000; // 10s real-time mode

export function cacheDom(): void {
  elScroll = document.getElementById("alerts-scroll");
  elBadge = document.getElementById("alerts-badge");
  elSpark = document.getElementById("alerts-count-spark") as SVGElement | null;
  if (elBadge) {
    elBadge.addEventListener("click", () => clearUnreadAlerts());
    elBadge.style.cursor = "pointer";
  }
}

export function clearUnreadAlerts(): void {
  _unread = 0;
  if (elBadge) {
    elBadge.textContent = "0";
    elBadge.style.display = "none";
  }
  updateTitle();
}

// ── Sound notification (AudioContext beep) ──
function playBeep(): void {
  try {
    const AudioCtor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return;
    const ctx = new AudioCtor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.value = _beepVolume / 100;
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
    ctx.close().catch(() => {});
  } catch {
    // AudioContext not available — silent fail
  }
}

// ── Desktop notification ──
function notify(data: AlertEvent[]): void {
  playBeep();
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const cities =
    data[0]?.alerts
      ?.flatMap((a) => a.cities ?? [])
      .slice(0, 3)
      .join(", ") ?? "אזורים שונים";
  try {
    new Notification("⚠️ צבע אדום", {
      body: cities,
      icon: "./favicon.ico",
      dir: "rtl",
      lang: "he",
    });
  } catch {
    // Notification failed — silent
  }
}

// ── Schedule next poll ──
function scheduleAlerts(): void {
  if (_timer) clearTimeout(_timer);
  const interval = _haveActive
    ? _realtimeMode
      ? ALERT_INTERVAL_RT
      : INTERVALS.ALERTS_ACTIVE
    : INTERVALS.ALERTS_IDLE;
  _timer = setTimeout(() => void loadAlerts(), interval);
}

// ── Fetch alerts (worker-first, then direct + proxy chain) ──
async function fetchAlerts(): Promise<AlertEvent[]> {
  // Worker-first: avoids CORS + uses edge cache
  if (isWorkerEnabled()) {
    try {
      const res = await fetchWithTimeout(`${WORKER_BASE_URL}/api/alerts`);
      if (res.ok) {
        const data = (await res.json()) as AlertEvent[];
        if (Array.isArray(data) && data.length) {
          diagLog("FDB-019: [alerts] worker OK");
          return data;
        }
      }
    } catch {
      diagLog("FDB-020: [alerts] worker failed, falling back to direct");
    }
  }

  // Fallback: direct + proxy chain
  const sources: Array<{ url: string; isAllOrigins: boolean }> = [
    { url: API.ALERTS, isAllOrigins: false },
    ...PROXIES.map((p) => ({
      url: p + encodeURIComponent(API.ALERTS),
      isAllOrigins: p.includes("allorigins"),
    })),
  ];

  for (const src of sources) {
    try {
      const res = await fetchWithTimeout(src.url);
      if (!res.ok) continue;
      const data = src.isAllOrigins
        ? (JSON.parse(((await res.json()) as { contents: string }).contents) as AlertEvent[])
        : ((await res.json()) as AlertEvent[]);
      if (Array.isArray(data) && data.length) return data;
    } catch {
      continue;
    }
  }
  return [];
}

// ── Build a single alert DOM element ──

/**
 * Return a color-coded threat level indicator icon.
 * threat=0/1 → 🔴 (rockets), threat=5 → 🟡 (hostile aircraft), unknown → 🟠
 */
export function alertThreatIcon(threat: number): string {
  if (threat === 5) return "🟡";
  if (threat <= 1) return "🔴";
  return "🟠";
}

/**
 * Return a Hebrew relative-age string for an alert timestamp.
 * `ageMin` is (now_unix - alert_unix) / 60.
 */
export function alertAgeLabel(ageMin: number): string {
  if (ageMin < 1) return "עכשיו";
  if (ageMin < 60) return `לפני ${ageMin}ד׳`;
  const h = Math.floor(ageMin / 60);
  return `לפני ${h}ש׳`;
}

export function buildAlertItem(
  ev: AlertEvent,
  now: number,
  highlightNew: boolean,
  isClone: boolean,
): HTMLElement | null {
  if (!ev.alerts?.length) return null;

  const firstAlert = ev.alerts[0];
  if (!firstAlert) return null;

  const allCities = ev.alerts.flatMap((a) => a.cities ?? []);
  const threat = ev.alerts.reduce((mx, a) => Math.max(mx, a.threat ?? 0), 0);
  const ageMin = Math.floor((now - firstAlert.time) / 60);

  const div = document.createElement("div");
  div.className =
    "alert-item" +
    (ageMin < 10 ? " active" : " past") +
    (highlightNew ? " new-alert" : "") +
    (isClone ? " clone" : "");

  const citiesEl = document.createElement("div");
  citiesEl.className = "alert-cities";
  const unique = [...new Set(allCities)];
  citiesEl.textContent =
    unique.length > 5
      ? unique.slice(0, 5).join(", ") + ` (+${unique.length - 5})`
      : unique.join(", ");
  div.appendChild(citiesEl);

  const metaEl = document.createElement("div");
  metaEl.className = "alert-meta";

  const timeEl = document.createElement("span");
  timeEl.className = "alert-time";
  const d = fromEpochSec(firstAlert.time);
  timeEl.textContent = d.toLocaleTimeString("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
  });
  metaEl.appendChild(timeEl);

  const thrEl = document.createElement("span");
  thrEl.className = "alert-threat";
  thrEl.textContent = `${alertThreatIcon(threat)} ${THREAT_LABELS[threat] ?? "⚠️ התרעה"}`;
  metaEl.appendChild(thrEl);

  // alert age label
  const ageEl = document.createElement("span");
  ageEl.className = "alert-age";
  ageEl.textContent = alertAgeLabel(ageMin);
  metaEl.appendChild(ageEl);

  div.appendChild(metaEl);

  return div;
}

// ── Render alerts list ──
export function renderAlerts(data: AlertEvent[], highlightNew: boolean): void {
  if (!elScroll) return;

  if (highlightNew) {
    _unread++;
    if (elBadge) {
      elBadge.textContent = String(_unread);
      elBadge.style.display = "";
    }
    updateTitle();
  }

  const now = Date.now() / 1000;
  let total24h = 0;
  for (const ev of data) {
    for (const a of ev.alerts ?? []) {
      if (now - a.time < 86_400) total24h++;
    }
  }

  const recent = data.slice(0, 25);
  const hasActive = data.some((ev) => ev.alerts?.some((a) => now - a.time < 600));

  // X12: publish active-alerts signal for sibling consumers.
  const activeAlerts: { area: string; ts: number }[] = [];
  for (const ev of data) {
    for (const a of ev.alerts ?? []) {
      if (now - a.time < 600) {
        for (const city of a.cities) activeAlerts.push({ area: city, ts: a.time });
      }
    }
  }
  if (activeAlerts.length > 0) {
    const areas = Array.from(new Set(activeAlerts.map((a) => a.area)));
    const latestTs = activeAlerts.reduce((m, a) => Math.max(m, a.ts), 0);
    setCardSignal("alerts", "active", { count: activeAlerts.length, areas, latestTs });
    _activeAlertsSnapshot = { count: activeAlerts.length, areas, latestTs };
  } else {
    setCardSignal("alerts", "active", null);
    _activeAlertsSnapshot = null;
  }

  const frag = document.createDocumentFragment();
  for (const isClone of [false, true]) {
    const counter = document.createElement("div");
    counter.className = "alert-count" + (isClone ? " clone" : "");
    counter.textContent = `🚨 ${total24h} התרעות ב-24 שעות האחרונות`;
    if (hasActive && !isClone) {
      const dot = document.createElement("span");
      dot.className = "alert-live-dot";
      dot.title = "התרעה פעילה";
      counter.appendChild(dot);
    }
    frag.appendChild(counter);

    if (!recent.length) {
      const empty = document.createElement("div");
      empty.className = "alert-count";
      empty.textContent = "✅ אין התרעות אחרונות";
      frag.appendChild(empty);
    } else {
      for (let i = 0; i < recent.length; i++) {
        const item = buildAlertItem(
          recent[i] as AlertEvent,
          now,
          highlightNew && i === 0 && !isClone,
          isClone,
        );
        if (item) frag.appendChild(item);
      }
    }
  }

  elScroll.replaceChildren(frag);

  // Scroll animation — disabled when user prefers reduced motion
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    elScroll.style.animation = "none";
    return;
  }
  const h = elScroll.scrollHeight / 2;
  const dur = Math.max(25, recent.length * 4);
  const style =
    document.getElementById("alerts-scroll-style") ??
    (() => {
      const s = document.createElement("style");
      s.id = "alerts-scroll-style";
      document.head.appendChild(s);
      return s;
    })();
  style.textContent = `@keyframes alertsScroll { from{transform:translateY(0) translateZ(0)} to{transform:translateY(-${h}px) translateZ(0)} }`;
  elScroll.style.animation = `alertsScroll ${dur}s linear infinite`;
}

// ── Main load function ──
export async function loadAlerts(): Promise<void> {
  if (!_enabled) return;
  if (!isPageVisible()) {
    scheduleAlerts();
    return;
  }

  setSync("alerts", "loading");

  const key = "alerts";
  const stale = cGetStale<AlertsResponse>(key);
  if (stale && Array.isArray(stale)) renderAlerts(stale, false);

  try {
    const data = await fetchAlerts();
    // Runtime validation: discard any structurally invalid events
    const validData = data.filter(isAlertEvent);
    if (validData.length !== data.length) {
      diagLog(`FDB-054: [alerts] Discarded ${data.length - validData.length} invalid event(s)`);
    }
    if (validData.length) {
      const newTopId = validData[0]?.id ?? null;
      const isNew = _lastAlertId !== null && newTopId !== _lastAlertId;
      _lastAlertId = newTopId;
      if (isNew) notify(validData);
      if (isNew) showAlertTakeover(validData); // await cSetAsync(key, validData);
      renderAlerts(validData, isNew);
      // persist to ring buffer
      alertRingAppend(validData);
      setSync("alerts", "ok");
      syncBurst("alerts");
      recordSuccess("alerts");
      markFresh("alerts");

      // Record alert count for 7-day sparkline
      await historyAppend("alerts:count", validData.length);
      const sparkVals = await historyGet("alerts:count", 7);
      if (elSpark !== null && sparkVals.length >= 2) {
        elSpark.innerHTML = trustedHTML(sparklineSvg(sparkVals, "var(--negative)"));
      }

      const now = Date.now() / 1000;
      _haveActive = validData.some((ev) => ev.alerts?.some((a) => now - a.time < 600));
    } else {
      _haveActive = false;
      setSync("alerts", stale ? "ok" : "error");
      recordFailure("alerts");
    }
  } catch (err) {
    diagLog(`FDB-021: [alerts] Error: ${String(err)}`);
    setSync("alerts", stale ? "ok" : "error");
    recordFailure("alerts");
  }

  scheduleAlerts();
}

export function setAlertsEnabled(enabled: boolean): void {
  _enabled = enabled;
  if (!enabled && _timer) {
    clearTimeout(_timer);
    _timer = null;
  }
}

export function toggleAlerts(): void {
  setAlertsEnabled(!_enabled);
}

export function isAlertsEnabled(): boolean {
  return _enabled;
}

export function setAlertsRealtime(on: boolean): void {
  _realtimeMode = on;
}

// ── SSE connection  ──

/**
 * Open a Server-Sent Events connection to the ALERTS_DO for real-time push.
 * Falls back to the existing polling loop if SSE is unavailable or errors.
 */
export function initAlertsSSE(): void {
  if (!isWorkerEnabled() || typeof EventSource === "undefined") return;
  _sse?.close();

  const url = `${WORKER_BASE_URL}/api/alerts/subscribe`;
  const es = new EventSource(url); // owasp-allow:A10 — URL from hardcoded WORKER_BASE_URL constant

  es.addEventListener("ping", () => {
    setAlertsRealtime(true);
    diagLog("FDB-026: [alerts] SSE connected");
  });

  es.addEventListener("alert", () => {
    void loadAlerts();
  });

  es.onerror = () => {
    setAlertsRealtime(false);
    es.close();
    _sse = null;
    diagLog("FDB-027: [alerts] SSE disconnected, polling fallback active");
  };

  _sse = es;
}

/** Close the SSE connection and reset realtime mode. */
export function destroyAlertsSSE(): void {
  if (_sse) {
    _sse.close();
    _sse = null;
  }
  setAlertsRealtime(false);
}

// 24-h alert history ring buffer ──────────────────────

// Full-screen alert takeover dialog ───────────────────

const TAKEOVER_AUTO_CLOSE_MS = 30_000; // auto-dismiss after 30s
let _takeoverTimer: ReturnType<typeof setTimeout> | null = null;
let _takeoverInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Show the full-screen alert takeover dialog for the given alert events.
 * Auto-dismisses after 30 seconds. Uses <dialog>.showModal().
 */
export function showAlertTakeover(events: AlertEvent[]): void {
  const dialog = document.getElementById("alerts-takeover") as HTMLDialogElement | null;
  if (!dialog || typeof dialog.showModal !== "function") return;

  const firstAlert = events[0]?.alerts?.[0];
  if (!firstAlert) return;

  const allCities = events.flatMap((e) => e.alerts?.flatMap((a) => a.cities ?? []) ?? []);
  const uniqueCities = [...new Set(allCities)].slice(0, 6).join(", ");
  const threat = events.reduce(
    (mx, e) => Math.max(mx, e.alerts?.reduce((m, a) => Math.max(m, a.threat ?? 0), 0) ?? 0),
    0,
  );

  const citiesEl = document.getElementById("alerts-takeover-cities");
  const threatEl = document.getElementById("alerts-takeover-threat");
  const countdownEl = document.getElementById("alerts-takeover-countdown");
  const closeBtn = document.getElementById("alerts-takeover-close");

  if (citiesEl) citiesEl.textContent = uniqueCities || "אזורים שונים";
  if (threatEl)
    threatEl.textContent = `${alertThreatIcon(threat)} ${THREAT_LABELS[threat] ?? "⚠️ התרעה"}`;

  // Countdown ticker
  if (_takeoverInterval) clearInterval(_takeoverInterval);
  let remaining = Math.ceil(TAKEOVER_AUTO_CLOSE_MS / 1000);
  const tick = (): void => {
    if (countdownEl) countdownEl.textContent = `סוגר בעוד ${remaining}ש׳`;
    remaining--;
  };
  tick();
  _takeoverInterval = setInterval(tick, 1000);

  // Auto-close
  if (_takeoverTimer) clearTimeout(_takeoverTimer);
  _takeoverTimer = setTimeout(() => hideAlertTakeover(), TAKEOVER_AUTO_CLOSE_MS);

  // Wire close button (idempotent)
  if (closeBtn) closeBtn.onclick = () => hideAlertTakeover();

  try {
    dialog.showModal();
  } catch {
    // showModal can throw if already open — ignore
  }
}

/** Close the takeover dialog and clear timers. */
export function hideAlertTakeover(): void {
  const dialog = document.getElementById("alerts-takeover") as HTMLDialogElement | null;
  if (dialog?.open) dialog.close();
  if (_takeoverTimer) {
    clearTimeout(_takeoverTimer);
    _takeoverTimer = null;
  }
  if (_takeoverInterval) {
    clearInterval(_takeoverInterval);
    _takeoverInterval = null;
  }
}

const ALERT_RING_KEY = "fdb_alert_ring";
const ALERT_RING_MAX = 100;
const ALERT_RING_TTL = 86_400_000; // 24h in ms

/**
 * Append new alert events to the localStorage ring buffer.
 * Deduplicates by event ID, prunes entries older than 24h, caps at 100.
 */
export function alertRingAppend(events: AlertEvent[]): void {
  const ring = alertRingGet();
  const cutoff = Date.now() - ALERT_RING_TTL;
  // Add new events not already in ring
  const existingIds = new Set(ring.map((e) => String(e.id ?? "")));
  for (const ev of events) {
    const evId = String(ev.id ?? "");
    if (evId && existingIds.has(evId)) continue;
    const evTime = (ev.alerts?.[0]?.time ?? 0) * 1000;
    if (evTime < cutoff) continue;
    ring.unshift(ev);
    if (evId) existingIds.add(evId);
  }
  // Prune stale entries and cap
  const fresh = ring.filter((e) => (e.alerts?.[0]?.time ?? 0) * 1000 >= cutoff);
  const capped = fresh.slice(0, ALERT_RING_MAX);
  try {
    localStorage.setItem(ALERT_RING_KEY, JSON.stringify(capped));
  } catch {
    // localStorage full — skip silently
  }
}

/**
 * Read the 24-h ring buffer from localStorage.
 * Returns up to 100 AlertEvent entries from the last 24 hours.
 */
export function alertRingGet(): AlertEvent[] {
  try {
    const raw = localStorage.getItem(ALERT_RING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - ALERT_RING_TTL;
    return (parsed as AlertEvent[]).filter(
      (e) => isAlertEvent(e) && (e.alerts?.[0]?.time ?? 0) * 1000 >= cutoff,
    );
  } catch {
    return [];
  }
}

/**
 * Render the 24-h ring buffer into the given container element.
 * Shows a message when empty.
 */
export function renderAlertHistory(container: HTMLElement): void {
  const ring = alertRingGet();
  const frag = document.createDocumentFragment();

  if (!ring.length) {
    const empty = document.createElement("div");
    empty.className = "alert-history-empty";
    empty.textContent = "✅ אין התרעות ב-24 שעות האחרונות";
    frag.appendChild(empty);
  } else {
    const now = Date.now() / 1000;
    for (const ev of ring) {
      const item = buildAlertItem(ev, now, false, false);
      if (item) frag.appendChild(item);
    }
  }
  container.replaceChildren(frag);
}

export function initAlertsCard(): void {
  cacheDom();
  registerSemanticProducer("alerts", buildAlertsPayload);
  const hd = document.querySelector('[data-card-id="alerts"] .card-hd-title');
  if (hd) renderFreshnessBadge("alerts", hd as HTMLElement);
  initAlertsSSE();
  void loadAlerts();
  // wire history toggle button
  const histBtn = document.getElementById("alerts-history-btn");
  const histPanel = document.getElementById("alerts-history-panel");
  if (histBtn && histPanel) {
    histBtn.addEventListener("click", () => {
      const isOpen = !histPanel.classList.contains("is-hidden");
      histPanel.classList.toggle("is-hidden", isOpen);
      histBtn.setAttribute("aria-expanded", String(!isOpen));
      if (!isOpen) renderAlertHistory(histPanel);
    });
  }
  diagLog("FDB-022: [alerts] Initialized");
}

export function destroyAlertsCard(): void {
  if (_timer !== null) {
    clearTimeout(_timer);
    _timer = null;
  }
  destroyAlertsSSE();
}

// configSchema ────────────────────────────────────────────────

export const alertsConfigSchema: CardConfigField[] = [
  {
    key: "alertsEnabled",
    labelHe: "התראות פעילות",
    labelEn: "Alerts enabled",
    type: "boolean",
    defaultValue: true,
    group: "כללי",
    groupOpenByDefault: true,
  },
  {
    key: "alertSound",
    labelHe: "צליל התראה",
    labelEn: "Alert sound",
    type: "boolean",
    defaultValue: true,
    group: "כללי",
  },
  {
    key: "alertVolume",
    labelHe: "עוצמת צליל",
    labelEn: "Alert volume",
    type: "range",
    defaultValue: 18,
    min: 0,
    max: 100,
    step: 1,
    group: "כללי",
  },
  {
    key: "realtimeAlerts",
    labelHe: "מצב זמן-אמת",
    labelEn: "Realtime mode",
    type: "boolean",
    defaultValue: false,
    group: "כללי",
  },
  // zone + history/severity/dim settings ─────────────
  {
    key: "alertZone",
    labelHe: "אזור גיאוגרפי",
    labelEn: "Geographic zone",
    type: "text",
    defaultValue: "",
    placeholder: "ירושלים",
    group: "סינון",
    groupOpenByDefault: true,
  },
  {
    key: "alertShowHistory",
    labelHe: "הצג היסטוריית התראות",
    labelEn: "Show alert history",
    type: "boolean",
    defaultValue: false,
    group: "סינון",
  },
  {
    key: "alertSeverityFilter",
    labelHe: "רמת חומרה מינימלית",
    labelEn: "Minimum severity",
    type: "select",
    defaultValue: "all",
    options: [
      { value: "all", label: "הכל / All" },
      { value: "moderate", label: "בינוני / Moderate" },
      { value: "severe", label: "חמור / Severe" },
      { value: "extreme", label: "קיצוני / Extreme" },
    ],
    group: "סינון",
  },
  {
    key: "alertDimOnAlert",
    labelHe: "עמעם תצוגה בהתראה",
    labelEn: "Dim display on alert",
    type: "boolean",
    defaultValue: false,
    group: "סינון",
  },
];

// ── Test isolation (Stream G.1) ───────────────────────────────────────────────

/**
 * Reset all module-level mutable state to defaults.
 * Call this in `beforeEach` instead of `vi.resetModules()` to avoid costly
 * module re-evaluation in tests.
 *
 * @internal — test use only
 */
export function _resetAlertsForTest(): void {
  _enabled = true;
  _lastAlertId = null;
  _haveActive = false;
  _unread = 0;
  if (_timer !== null) {
    clearTimeout(_timer);
    _timer = null;
  }
  if (_takeoverTimer !== null) {
    clearTimeout(_takeoverTimer);
    _takeoverTimer = null;
  }
  if (_takeoverInterval !== null) {
    clearInterval(_takeoverInterval);
    _takeoverInterval = null;
  }
  _realtimeMode = false;
  _beepVolume = 18;
  _sse?.close();
  _sse = null;
  elScroll = null;
  elBadge = null;
  elSpark = null;
  document.title = "FamilyDashBoard";
}
